import { HAND_SIZE } from "@bing/engine-pasur";
import { useLayoutEffect, useRef, type RefObject } from "react";
import { prefersReducedMotion } from "./card-motion";
import {
  BURIAL_MS,
  PASUR_BURY_SHOW_MS,
  PASUR_DEAL_CARD_MS,
  PASUR_DEAL_FLIGHT_MS,
  type DealPlan,
} from "./pasur-moments";

/*
 * THE DEAL, OUT OF THE STOCK — the DOM side of a deal moment (P1 Task 6, W14; the operator's «always
 * shown» holds for the deal too). `pasur-moments.ts` says what is dealt and when (`dealPlan`); the hold
 * (`use-capture-hold.ts`) keeps it on screen, the hand disabled, until its last card has landed; this
 * flies every card OUT OF THE STOCK — the felt's corner chip, measured — on WAAPI, the table's own
 * elements marked for it:
 *
 *   `[data-deal-hand="<id>"]`    your four, the fan's own card places, in the fan's order, at your turn
 *                                in the deal;
 *   `[data-deal-back="<s>:<j>"]` another seat's four, face down, into that seat's avatar — in the deal
 *                                layer (`[data-deal-layer]`), placed at the seat here;
 *   `[data-deal-pool="<id>"]`    the opening's table — the pool's own slots;
 *   `[data-deal-bury="<j>"]`     a buried Jack (D6): out of the stock, onto the table's last place, a
 *                                stay there (every seat sees it), and back INTO the stock — a plain
 *                                return to the stock's own centre: where it goes in the pack is never
 *                                shown (D6b) — before the card that replaced it is laid.
 *
 * Every flight starts at the stock, hidden until it leaves (`fill: "backwards"` — its first frame
 * through the delay and NOTHING once landed, never a held fill: Card's blur rule), and lands exactly
 * on its element's resting transform. A runtime without `.animate` (happy-dom, SSR) or a reduced-motion
 * setting draws the dealt table at once; the hold still lasts. An invisible TAIL on the deal layer runs
 * past the deal's end, so whatever waits on the page's animations (the layout probe's settle) waits for
 * the hold's release, never only for the last card's landing.
 */

/** The deal's flights' WAAPI id (what tells them from the hold's collect). */
export const DEAL_FLIGHT_ID = "pasur-deal";
/** The tail's id. */
export const DEAL_TAIL_ID = "pasur-deal-tail";
/** The tail runs this far past the deal's end, invisible: the release, never its own finish, ends it. */
const DEAL_TAIL_MS = 120;
/** `card-throw`'s easing: a dealt card arrives like a thrown one. */
const EASING = "cubic-bezier(.2,.7,.3,1)";

interface Point {
  readonly x: number;
  readonly y: number;
}

const centreOf = (el: Element): Point => {
  const r = el.getBoundingClientRect();
  return { x: r.left + r.width / 2, y: r.top + r.height / 2 };
};
const px = (n: number): string => `${Math.round(n * 100) / 100}px`;
/** The vector from `to` back to the stock (screen axes). */
const fromStock = (stock: Point, to: Point): Point => ({ x: stock.x - to.x, y: stock.y - to.y });

/** A card out of the stock onto its resting place — `rest` is its element's own resting transform,
 *  so the leading translate is in its parent's (unturned) axes, the screen's, and its first frame is
 *  centred ON the stock whatever the element's transform-origin (no scale: a hand card turns about its
 *  foot, and a scale there would start it off the stock). */
export function dealFlightFrames(from: Point, rest: string): Keyframe[] {
  return [
    { transform: `translate(${px(from.x)}, ${px(from.y)}) ${rest}`, opacity: 0 },
    { opacity: 1, offset: 0.12 },
    { transform: rest, opacity: 1 },
  ];
}

/** A face-down card out of the stock into a seat (its element is placed AT the seat). */
function intoSeatFrames(from: Point): Keyframe[] {
  return [
    { transform: `translate(${px(from.x)}, ${px(from.y)}) scale(0.8)`, opacity: 0 },
    { opacity: 1, offset: 0.12 },
    { transform: "translate(0px, 0px) scale(0.6)", opacity: 1, offset: 0.82 },
    { transform: "translate(0px, 0px) scale(0.4)", opacity: 0 },
  ];
}

/** A buried Jack: out of the stock, onto the table's last place (`rest`, its tilt), a stay, and back to
 *  the stock's centre — a plain return. */
function buryFrames(from: Point, rest: string): Keyframe[] {
  const out = `translate(${px(from.x)}, ${px(from.y)}) ${rest} scale(0.6)`;
  const flight = PASUR_DEAL_FLIGHT_MS / BURIAL_MS;
  const stayed = (PASUR_DEAL_FLIGHT_MS + PASUR_BURY_SHOW_MS) / BURIAL_MS;
  return [
    { transform: out, opacity: 0 },
    { opacity: 1, offset: 0.12 * flight },
    { transform: rest, opacity: 1, offset: flight },
    { transform: rest, opacity: 1, offset: stayed },
    { transform: out, opacity: 1, offset: stayed + 0.88 * (1 - stayed) },
    { transform: out, opacity: 0 },
  ];
}

/**
 * `usePasurDeal({ plan, id, root, you, relayout })` — call it in the TABLE with the deal on screen
 * (`plan`, null when none) and its hold id. Re-aimed when `relayout` changes while it runs (the pool's
 * first measured room lands in the same pre-paint pass as a mount's deal): the flights are re-made at
 * the same point of the deal, so nothing on screen jumps.
 */
export function usePasurDeal(opts: {
  plan: DealPlan | null;
  id: number;
  root: RefObject<HTMLElement>;
  you: number;
  relayout: string;
}): void {
  const { plan, id, root, you, relayout } = opts;
  const started = useRef<{ id: number; at: number }>({ id: 0, at: 0 });
  useLayoutEffect(() => {
    if (plan === null || id === 0) return undefined;
    if (started.current.id !== id) started.current = { id, at: Date.now() };
    const elapsed = Date.now() - started.current.at;
    const table = root.current;
    if (!table || prefersReducedMotion()) return undefined;
    const stockEl = table.querySelector('[aria-label="deck"]');
    if (!stockEl) return undefined;
    const stock = centreOf(stockEl);
    const anims: Animation[] = [];
    const fly = (el: HTMLElement, frames: Keyframe[], at: number, duration: number): void => {
      if (typeof el.animate !== "function") return;
      anims.push(
        el.animate(frames, {
          id: DEAL_FLIGHT_ID,
          duration,
          delay: at - elapsed,
          easing: EASING,
          fill: "backwards",
        }),
      );
    };
    // your four: the fan's own cards, in its order, at your turn in the deal
    const yours = plan.seats.find((p) => p.seat === you);
    if (yours)
      table.querySelectorAll<HTMLElement>("[data-deal-hand]").forEach((el, i) => {
        const rest = el.style.transform;
        fly(
          el,
          dealFlightFrames(fromStock(stock, centreOf(el)), rest),
          yours.at + i * PASUR_DEAL_CARD_MS,
          PASUR_DEAL_FLIGHT_MS,
        );
      });
    // the opening's table: its own slots
    for (const p of plan.pool) {
      const el = table.querySelector<HTMLElement>(`[data-deal-pool="${p.card}"]`);
      if (el)
        fly(
          el,
          dealFlightFrames(fromStock(stock, centreOf(el)), el.style.transform),
          p.at,
          PASUR_DEAL_FLIGHT_MS,
        );
    }
    const layer = table.querySelector<HTMLElement>("[data-deal-layer]");
    if (layer) {
      const origin = layer.getBoundingClientRect();
      const place = (el: HTMLElement, at: Point): void => {
        el.style.left = px(at.x - origin.left);
        el.style.top = px(at.y - origin.top);
      };
      // the other seats' fours, face down, into each seat
      for (const p of plan.seats) {
        if (p.seat === you) continue;
        const seatEl =
          table.querySelector(`[data-seat="${p.seat}"] .avatar`) ??
          table.querySelector(`[data-seat="${p.seat}"]`);
        if (!seatEl) continue;
        const target = centreOf(seatEl);
        for (let j = 0; j < HAND_SIZE; j++) {
          const el = layer.querySelector<HTMLElement>(`[data-deal-back="${p.seat}:${j}"]`);
          if (!el) continue;
          place(el, target);
          fly(
            el,
            intoSeatFrames(fromStock(stock, target)),
            p.at + j * PASUR_DEAL_CARD_MS,
            PASUR_DEAL_FLIGHT_MS,
          );
        }
      }
      // the buried Jacks, in the table's last place, and back into the stock
      const slotCard = plan.burySlot === null ? undefined : plan.pool[plan.burySlot]?.card;
      const slot =
        slotCard === undefined
          ? null
          : table.querySelector<HTMLElement>(`[data-deal-pool="${slotCard}"]`);
      if (slot) {
        const at = centreOf(slot);
        plan.buried.forEach((b, j) => {
          const el = layer.querySelector<HTMLElement>(`[data-deal-bury="${j}"]`);
          if (!el) return;
          place(el, at);
          fly(el, buryFrames(fromStock(stock, at), slot.style.transform), b.at, BURIAL_MS);
        });
      }
      // THE TAIL: past the deal's end, invisible — the hold's release (this effect's cleanup) ends it
      if (typeof layer.animate === "function")
        anims.push(
          layer.animate([{ opacity: 1 }, { opacity: 1 }], {
            id: DEAL_TAIL_ID,
            duration: Math.max(0, plan.ms - elapsed) + DEAL_TAIL_MS,
          }),
        );
    }
    return () => {
      for (const a of anims) a.cancel();
    };
  }, [plan, id, root, you, relayout]);
}
